import { useQueryClient } from 'react-query';
import { useToastify } from '../../../../../../Hooks';
import useFormServerErrors from '../../../../../../Hooks/useFormServerErrors';
import {
    useFrontendKendoGridDataHandler,
    useKendoGrid,
    useKendoGridInlineEditing,
} from '../../../../../../Hooks/useKendoGrid';

import { trimStrings } from '../../../../../Licenses/licenseHelper';
import {
    useCreateDocumentLinkManagement,
    useDeleteDocumentLinkManagement,
    useUpdateDocumentLinkManagement,
} from '../../hooks/useDocumentLinkManagement';

const idField = 'id';
const editField = 'inEdit';

const prepareData = (data: any) => ({
    ...data,
    sourceDirectory: data.sourceDirectory?.fullPath,
    linkDirectory: data.linkDirectory?.fullPath,
    disciplineCode: data.disciplineCode?.asiteCustomAttributeCode,
    organisation: data.organisation?.asiteCustomAttributeCode,
    documentType: data.documentType?.asiteCustomAttributeCode,
    status: data.status?.statusId,
    isSharedPlaceholder: Boolean(data.isSharedPlaceholder),
    linkType: data.linkType?.id,
    sourceProjectName: data.sourceDirectory?.workspaceName,
    targetProjectName: data.linkDirectory?.workspaceName,
    purposeOfIssue: data.purposeOfIssue?.purposeOfIssueId,
    object: data.object?.asiteCustomAttributeCode,
    deelproject: data.deelproject?.asiteCustomAttributeCode,
});

const useDocumentLinkManagement = (dgs: any) => {
    const queryClient = useQueryClient();
    const { gridState, gridResult, setGridState, changeGridResult } = useKendoGrid({
        source: dgs as any[],
    });
    const handleDataStateChange = useFrontendKendoGridDataHandler({
        setGridState,
        changeGridResult,
        data: dgs as any[],
    });
    const { showToastError, showToastSuccess } = useToastify();
    const { addNew, add, discard, enterEdit, update, cancel, remove, handleItemChange } = useKendoGridInlineEditing({
        idField,
        editField,
        source: dgs as any[],
        data: gridResult.data,
        setData: changeGridResult,
    });

    const createDocumentLinkManagementMutation = useCreateDocumentLinkManagement();
    const deleteDocumentLinkManagementMutation = useDeleteDocumentLinkManagement();
    const updateDocumentLinkMutation = useUpdateDocumentLinkManagement();
    const { mutate: createDocumentLinkManagement } = createDocumentLinkManagementMutation;
    const { mutate: deleteDocumentLinkManagement } = deleteDocumentLinkManagementMutation;
    const { mutate: updateDocumentLink } = updateDocumentLinkMutation;
    const { hasError, getCustomErrorMessageProductReservation } = useFormServerErrors();
    const addItem = (dataItem: any) => {
        const model = {
            ...prepareData(dataItem),
            targetFolderId: dataItem.linkDirectory?.folderId,
            sourceFolderId: dataItem.sourceDirectory?.folderId,
        };
        delete model?.id;
        trimStrings(model);
        createDocumentLinkManagement(model, {
            onSuccess(response: any) {
                if (hasError(response)) {
                    showToastError(getCustomErrorMessageProductReservation(response));
                } else {
                    showToastSuccess('Document link added!');
                    add(dataItem);
                    queryClient.invalidateQueries(['document-links']);
                }
            },
            onError(error: any) {
                showToastError(getCustomErrorMessageProductReservation(error?.response?.data));
            },
        });
    };

    const deleteItem = (dataItem: any) => {
        deleteDocumentLinkManagement(dataItem[idField], {
            onSuccess(response: any) {
                if (hasError(response)) {
                    showToastError(getCustomErrorMessageProductReservation(response));
                } else {
                    showToastSuccess('Document link deleted!');
                    remove(dataItem);
                    queryClient.invalidateQueries(['document-links']);
                }
            },
            onError(error: any) {
                showToastError(getCustomErrorMessageProductReservation(error?.response?.data));
            },
        });
    };

    const updateItem = (dataItem: any) => {
        delete dataItem.inEdit;
        const model = {
            ...prepareData(dataItem),
            targetFolderId: dataItem.linkDirectory?.folderId ?? dataItem.targetFolderId,
            sourceFolderId: dataItem.sourceDirectory?.folderId ?? dataItem.sourceFolderId,
        };
        trimStrings(model);
        updateDocumentLink(model, {
            onSuccess(response: any) {
                if (hasError(response)) {
                    showToastError(getCustomErrorMessageProductReservation(response));
                } else {
                    showToastSuccess("Document link's data updated!");
                    update(dataItem);
                    queryClient.invalidateQueries(['document-links']);
                }
            },
            onError(error: any, variables: any, context: any) {
                showToastError(getCustomErrorMessageProductReservation(error?.response?.data));
                cancel(dataItem);
            },
        });
    };
    const validate = (dataItem: any) => {
        if (!dataItem.sourceDirectory) {
            showToastError('Source directory is required');
            return false;
        }
        if (!dataItem.documentType) {
            showToastError('File type is required');
            return false;
        }
        if (!dataItem.status) {
            showToastError('Status is required');
            return false;
        }
        if (!dataItem.organisation) {
            showToastError('Organisation is required');
            return false;
        }

        if (!dataItem.linkDirectory) {
            showToastError('Link directory is required');
            return false;
        }
        if (!dataItem.linkType) {
            showToastError('Link type is required');
            return false;
        }

        return true;
    };

    return {
        idField,
        editField,
        gridState,
        gridResult,
        setGridState,
        changeGridResult,
        handleDataStateChange,
        handleItemChange,
        validate,
        inlineEditing: { addNew, add: addItem, discard, enterEdit, update: updateItem, cancel, remove: deleteItem },
        mutations: {
            createDocumentLinkManagementMutation,
            deleteDocumentLinkManagementMutation,
        },
    };
};

export default useDocumentLinkManagement;
